import { parseZplLabels, qrPath, QUIET_ZONE_MODULES, type ParsedZplLabel, type ZplElement, type ZplRotation } from '@wms/domain';
import { base64, bitmapPng, inflateZ64 } from './png';

// ZPL labels drawn as SVG in printer dots: shown on screen and printed through any printer's
// driver at true size (8 dots per millimetre, 203 dpi), when the printer does not speak ZPL.
// Content that would run past the label's edge is brought onto it rather than cut off: a text
// narrowed along its line (textSqueeze), a code by shrinking the whole label (labelFit).

export const DOTS_PER_MM = 8;

/** Width of a text in pixels at the given size; the browser measures with a canvas. */
export type Measure = (text: string, size: number, family: string, weight: number) => number;

const CONDENSED = "'Arial Narrow','Roboto Condensed','Liberation Sans Narrow',Arial,sans-serif";
const MONO = "Consolas,'Courier New',monospace";

let canvas: CanvasRenderingContext2D | null | undefined;
/** Canvas measurement in the browser, an estimate elsewhere (tests). */
export const defaultMeasure: Measure = (text, size, family, weight) => {
  if (canvas === undefined) canvas = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
  if (!canvas) return text.length * size * (family === MONO ? 0.6 : 0.47);
  canvas.font = `${weight} ${size}px ${family}`;
  return canvas.measureText(text).width;
};

const escapeXml = (text: string) => text.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' })[c]!);
const r = (n: number) => Math.round(n * 100) / 100;

/** Parses and turns :Z64: graphics into bitmaps (inflating is asynchronous in the browser). */
export async function loadZplLabels(zpl: string): Promise<ParsedZplLabel[]> {
  const labels = parseZplLabels(zpl);
  return Promise.all(
    labels.map(async (label) => ({
      ...label,
      elements: await Promise.all(
        label.elements.map(async (e) => {
          if (e.kind !== 'graphic' || !e.z64) return e;
          try {
            return { ...e, bitmap: await inflateZ64(e.z64), z64: null };
          } catch {
            return e;
          }
        }),
      ),
    })),
  );
}

/** Rotates a w×h field drawn at the local origin so its box keeps its top-left at (x, y). */
function rotate(x: number, y: number, rotation: ZplRotation, w: number, h: number): string {
  switch (rotation) {
    case 90:
      return `translate(${r(x + h)} ${r(y)}) rotate(90)`;
    case 180:
      return `translate(${r(x + w)} ${r(y + h)}) rotate(180)`;
    case 270:
      return `translate(${r(x)} ${r(y + w)}) rotate(270)`;
    default:
      return `translate(${r(x)} ${r(y)})`;
  }
}

/** A rectangle in label dots. */
interface Rect {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

/** Where a point (u, v) of a w×h field placed as rotate() places it lands on the label. */
function turned(x: number, y: number, rotation: ZplRotation, w: number, h: number, u: number, v: number): readonly [number, number] {
  switch (rotation) {
    case 90:
      return [x + h - v, y + u];
    case 180:
      return [x + w - u, y + h - v];
    case 270:
      return [x + v, y + w - u];
    default:
      return [x + u, y + v];
  }
}

/** The part of the label a local rectangle of such a field covers. */
function turnedRect(x: number, y: number, rotation: ZplRotation, w: number, h: number, local: Rect): Rect {
  const [ax, ay] = turned(x, y, rotation, w, h, local.x0, local.y0);
  const [bx, by] = turned(x, y, rotation, w, h, local.x1, local.y1);
  return { x0: Math.min(ax, bx), y0: Math.min(ay, by), x1: Math.max(ax, bx), y1: Math.max(ay, by) };
}

type TextElement = Extract<ZplElement, { kind: 'text' }>;
type BarsElement = Extract<ZplElement, { kind: 'bars' }>;

/** A text field's lines as drawn: each one's offset in its block and width, in dots. */
function textLayout(e: TextElement, measure: Measure) {
  const scalable = e.font === '0' || !/^[A-Z]$/.test(e.font);
  const family = scalable ? CONDENSED : MONO;
  const weight = scalable ? 700 : 400;
  // font 0 grows sideways with its width parameter; bitmap fonts are cells of about 0.6 em
  const sx = scalable ? e.width / e.height : e.width / e.height / 0.6;
  const measureLine = (line: string) => measure(line, e.height, family, weight) * sx;
  const lines = (e.block ? wrap(e.text, e.block.width, measureLine).slice(0, e.block.lines) : [e.text]).map((text) => {
    const width = measureLine(text);
    const dx = !e.block || e.block.justify === 'L' || e.block.justify === 'J' ? 0 : e.block.justify === 'C' ? (e.block.width - width) / 2 : e.block.width - width;
    return { text, width, dx };
  });
  const lineHeight = e.height + (e.block?.spacing ?? 0);
  const boxWidth = e.block ? e.block.width : (lines[0]?.width ?? 0);
  const boxHeight = lineHeight * lines.length;
  const ascent = e.height * 0.8;
  const top = e.anchor === 'baseline' && e.rotation === 0 ? e.y - ascent : e.y;
  return { family, weight, sx, lines, lineHeight, boxWidth, boxHeight, ascent, top };
}

type TextLayout = ReturnType<typeof textLayout>;

/** What may stick out by rounding without anything being lost, in dots. */
const FIT_TOLERANCE = 2;
/** A text is narrowed to half its width at most; what still sticks out shrinks the label. */
const MIN_SQUEEZE = 0.5;

/**
 * A text that would run past the label's edge along its line (a long name, a font wider than the
 * printer's) is narrowed toward where its block holds it (its start, centre or end) until it ends
 * at the edge: the text stays whole and the label's codes stay at true size. `k`: 1 when it fits.
 */
function textSqueeze(e: TextElement, t: TextLayout, label: ParsedZplLabel): { readonly k: number; readonly anchor: number } {
  const justify = e.block?.justify ?? 'L';
  const anchor = justify === 'C' ? t.boxWidth / 2 : justify === 'R' ? t.boxWidth : 0;
  if (t.lines.length === 0) return { k: 1, anchor };
  // where the line runs on the label: across it (0°, 180°) or down it (90°, 270°), forward or back
  const down = e.rotation === 90 || e.rotation === 270;
  const back = e.rotation === 180 || e.rotation === 270;
  const length = down ? label.height : label.width;
  const origin = (down ? t.top : e.x) + (back ? t.boxWidth : 0);
  const at = (u: number) => origin + (back ? -u : u);
  const held = at(anchor);
  if (held < 0 || held > length) return { k: 1, anchor };
  let k = 1;
  for (const u of [Math.min(0, ...t.lines.map((l) => l.dx)), Math.max(...t.lines.map((l) => l.dx + l.width))]) {
    const end = at(u);
    if (end > length + FIT_TOLERANCE) k = Math.min(k, (length - held) / (end - held));
    if (end < -FIT_TOLERANCE) k = Math.min(k, held / (held - end));
  }
  return { k: k < 1 ? Math.max(MIN_SQUEEZE, Math.floor(k * 1000) / 1000) : 1, anchor };
}

/** A barcode's human readable line and where its bars start, in dots. */
function barsLayout(e: BarsElement) {
  const narrowest = Math.min(...e.bars.bars.map((b) => b.w));
  const hriSize = Math.min(40, Math.max(16, narrowest * 10));
  const hri = e.hri === 'none' ? 0 : hriSize + 6;
  const top = e.anchor === 'baseline' && e.rotation === 0 ? e.y - e.height : e.y;
  return { narrowest, hriSize, hri, top, barsTop: e.hri === 'above' ? hri : 0 };
}

/**
 * The part of the label an element must keep: its text (narrowed as textSqueeze narrows it) or
 * code, a code with its quiet zone when it reaches past an edge (a scanner needs that space).
 * Null for rules, boxes and pictures: past an edge they are cut off, as a printer cuts them, and
 * do not shrink the label.
 */
function keptRect(e: ZplElement, measure: Measure, label: ParsedZplLabel): Rect | null {
  const off = (b: Rect) => b.x0 < 0 || b.y0 < 0 || b.x1 > label.width || b.y1 > label.height;
  switch (e.kind) {
    case 'text': {
      const t = textLayout(e, measure);
      if (t.lines.length === 0) return null;
      const { k, anchor } = textSqueeze(e, t, label);
      const narrowed = (u: number) => anchor + (u - anchor) * k;
      const local = {
        x0: narrowed(Math.min(0, ...t.lines.map((l) => l.dx))),
        y0: 0,
        x1: narrowed(Math.max(...t.lines.map((l) => l.dx + l.width))),
        y1: t.boxHeight,
      };
      return turnedRect(e.x, t.top, e.rotation, t.boxWidth, t.boxHeight, local);
    }
    case 'bars': {
      const b = barsLayout(e);
      const h = e.height + b.hri;
      const ink = turnedRect(e.x, b.top, e.rotation, e.bars.width, h, { x0: 0, y0: 0, x1: e.bars.width, y1: h });
      if (!off(ink)) return ink;
      const quiet = QUIET_ZONE_MODULES * b.narrowest;
      return turnedRect(e.x, b.top, e.rotation, e.bars.width, h, { x0: -quiet, y0: 0, x1: e.bars.width + quiet, y1: h });
    }
    case 'qr': {
      const size = e.code.size * e.module;
      const top = e.anchor === 'baseline' ? e.y - size : e.y;
      const ink = { x0: e.x, y0: top, x1: e.x + size, y1: top + size };
      // a QR code's quiet zone is 4 modules
      const quiet = 4 * e.module;
      return off(ink) ? { x0: ink.x0 - quiet, y0: ink.y0 - quiet, x1: ink.x1 + quiet, y1: ink.y1 + quiet } : ink;
    }
    case 'unsupported': {
      const top = e.anchor === 'baseline' ? e.y - e.h : e.y;
      return { x0: e.x, y0: top, x1: e.x + e.w, y1: top + e.h };
    }
    default:
      return null;
  }
}

/** How a label is drawn onto its page: shrunk by `scale` (1: true size) and moved by dx, dy dots. */
export interface LabelFit {
  readonly scale: number;
  readonly dx: number;
  readonly dy: number;
}

/**
 * A safety net under any label, ours or a marketplace's: when its codes would run past the
 * label's edge (with their quiet zones), or a text even narrowed to half, the whole label is
 * shrunk evenly until they are on it, centred, instead of being cut off. A label that fits is
 * drawn 1:1, every bar on the printer's own dots. A field wholly off the label stays off, as a
 * printer leaves it.
 */
export function labelFit(label: ParsedZplLabel, measure: Measure = defaultMeasure): LabelFit {
  let x0 = 0;
  let y0 = 0;
  let x1 = label.width;
  let y1 = label.height;
  for (const e of label.elements) {
    const b = keptRect(e, measure, label);
    if (!b || b.x0 >= label.width || b.y0 >= label.height || b.x1 <= 0 || b.y1 <= 0) continue;
    x0 = Math.min(x0, b.x0);
    y0 = Math.min(y0, b.y0);
    x1 = Math.max(x1, b.x1);
    y1 = Math.max(y1, b.y1);
  }
  if (x0 >= -FIT_TOLERANCE && y0 >= -FIT_TOLERANCE && x1 <= label.width + FIT_TOLERANCE && y1 <= label.height + FIT_TOLERANCE) return { scale: 1, dx: 0, dy: 0 };
  // rounded down, so the shrunk content never reaches past the edge
  const scale = Math.floor(Math.min(label.width / (x1 - x0), label.height / (y1 - y0)) * 10_000) / 10_000;
  return { scale, dx: r((label.width - (x1 - x0) * scale) / 2 - x0 * scale), dy: r((label.height - (y1 - y0) * scale) / 2 - y0 * scale) };
}

function wrap(text: string, width: number, measureLine: (line: string) => number): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split('\\&')) {
    let line = '';
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const next = line ? `${line} ${word}` : word;
      if (line && measureLine(next) > width) {
        lines.push(line);
        line = word;
      } else line = next;
    }
    lines.push(line);
  }
  return lines;
}

function element(e: ZplElement, measure: Measure, label: ParsedZplLabel): string {
  const ink = e.reverse ? '#fff' : '#000';
  switch (e.kind) {
    case 'box': {
      const top = e.anchor === 'baseline' ? e.y - e.h : e.y;
      const fill = e.white ? '#fff' : ink;
      const radius = (e.rounding / 8) * (Math.min(e.w, e.h) / 2);
      if (e.thickness * 2 >= Math.min(e.w, e.h)) return `<rect x="${e.x}" y="${top}" width="${e.w}" height="${e.h}" rx="${r(radius)}" fill="${fill}"/>`;
      const t = e.thickness;
      return `<rect x="${r(e.x + t / 2)}" y="${r(top + t / 2)}" width="${r(e.w - t)}" height="${r(e.h - t)}" rx="${r(radius)}" fill="none" stroke="${fill}" stroke-width="${t}"/>`;
    }
    case 'ellipse': {
      const top = e.anchor === 'baseline' ? e.y - e.h : e.y;
      const fill = e.white ? '#fff' : ink;
      const filled = e.thickness * 2 >= Math.min(e.w, e.h);
      const t = filled ? 0 : e.thickness;
      return `<ellipse cx="${r(e.x + e.w / 2)}" cy="${r(top + e.h / 2)}" rx="${r((e.w - t) / 2)}" ry="${r((e.h - t) / 2)}" ${filled ? `fill="${fill}"` : `fill="none" stroke="${fill}" stroke-width="${t}"`}/>`;
    }
    case 'diagonal': {
      const top = e.anchor === 'baseline' ? e.y - e.h : e.y;
      const t = e.thickness;
      const points =
        e.leaning === 'R'
          ? [[e.x, top + e.h], [e.x + t, top + e.h], [e.x + e.w, top], [e.x + e.w - t, top]]
          : [[e.x, top], [e.x + t, top], [e.x + e.w, top + e.h], [e.x + e.w - t, top + e.h]];
      return `<polygon points="${points.map((p) => p.join(',')).join(' ')}" fill="${e.white ? '#fff' : ink}"/>`;
    }
    case 'text': {
      const t = textLayout(e, measure);
      const { family, weight, sx, lines, lineHeight, boxWidth, boxHeight, ascent, top } = t;
      const tspans = lines
        .map((line, i) => `<text x="${r(line.dx / sx)}" y="${r(ascent + i * lineHeight)}" transform="scale(${r(sx)} 1)">${escapeXml(line.text)}</text>`)
        .join('');
      // past the label's edge: narrowed toward where its block holds it
      const { k, anchor } = textSqueeze(e, t, label);
      const drawn = k < 1 ? `<g transform="matrix(${k} 0 0 1 ${r(anchor * (1 - k))} 0)">${tspans}</g>` : tspans;
      return `<g transform="${rotate(e.x, top, e.rotation, boxWidth, boxHeight)}" font-family="${escapeXml(family)}" font-weight="${weight}" font-size="${e.height}" fill="${ink}">${drawn}</g>`;
    }
    case 'bars': {
      const { hriSize, hri, top, barsTop } = barsLayout(e);
      const rects = e.bars.bars.map((b) => `<rect x="${b.x}" y="${barsTop}" width="${b.w}" height="${e.height}"/>`).join('');
      const text =
        e.hri === 'none'
          ? ''
          : `<text x="${r(e.bars.width / 2)}" y="${e.hri === 'above' ? hriSize : e.height + hri - 4}" font-size="${hriSize}" font-family="${MONO}" text-anchor="middle">${escapeXml(e.bars.text)}</text>`;
      return `<g transform="${rotate(e.x, top, e.rotation, e.bars.width, e.height + hri)}" fill="${ink}" shape-rendering="crispEdges">${rects}${text}</g>`;
    }
    case 'qr': {
      const size = e.code.size * e.module;
      const top = e.anchor === 'baseline' ? e.y - size : e.y;
      return `<path transform="translate(${e.x} ${top}) scale(${e.module})" d="${qrPath(e.code)}" fill="${ink}" shape-rendering="crispEdges"/>`;
    }
    case 'graphic': {
      if (!e.bitmap) return '';
      const width = e.bytesPerRow * 8 * e.scale;
      const height = e.rows * e.scale;
      const top = e.anchor === 'baseline' ? e.y - height : e.y;
      const png = base64(bitmapPng(e.bitmap, e.bytesPerRow, e.rows));
      return `<image x="${e.x}" y="${top}" width="${width}" height="${height}" preserveAspectRatio="none" style="image-rendering:pixelated" href="data:image/png;base64,${png}"/>`;
    }
    case 'unsupported': {
      const top = e.anchor === 'baseline' ? e.y - e.h : e.y;
      return `<g><rect x="${e.x}" y="${top}" width="${e.w}" height="${e.h}" fill="#eee" stroke="#999" stroke-dasharray="6 4"/><text x="${e.x + 8}" y="${top + 28}" font-size="22" font-family="${MONO}" fill="#666">${escapeXml(e.what)}</text></g>`;
    }
  }
}

/**
 * One label as an SVG document in printer dots (white background, ^POI turned around), at true
 * size, or shrunk onto the page when it would not fit (labelFit).
 */
export function labelSvg(label: ParsedZplLabel, measure: Measure = defaultMeasure): string {
  const fit = labelFit(label, measure);
  const drawn = label.elements.map((e) => element(e, measure, label)).join('');
  const body = fit.scale === 1 ? drawn : `<g transform="translate(${fit.dx} ${fit.dy}) scale(${fit.scale})">${drawn}</g>`;
  const content = label.inverted ? `<g transform="rotate(180 ${label.width / 2} ${label.height / 2})">${body}</g>` : body;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${label.width} ${label.height}" width="${r(label.width / DOTS_PER_MM)}mm" height="${r(label.height / DOTS_PER_MM)}mm"><rect width="${label.width}" height="${label.height}" fill="#fff"/>${content}</svg>`;
}

/** Physical size in millimetres (rounded to whole millimetres: label stock comes in those). */
export const labelSizeMm = (label: ParsedZplLabel) => ({ width: Math.round(label.width / DOTS_PER_MM), height: Math.round(label.height / DOTS_PER_MM) });
